import { useState, useEffect, useRef } from "react";

// ─── Mock Data ──────────────────────────────────────────────────────────────



// ─── Helpers ─────────────────────────────────────────────────────────────────

const FLAG = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  try { return String.fromCodePoint(...[...cc.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)); } catch { return "🌐"; }
};
const statusColor = (s) => { if (!s) return "#ef4444"; if (s < 300) return "#22c55e"; if (s < 400) return "#f59e0b"; return "#ef4444"; };
const latencyColor = (ms) => { if (!ms) return "#6b7280"; if (ms < 100) return "#22c55e"; if (ms < 250) return "#f59e0b"; return "#ef4444"; };
const hostFromUrl = (url) => { try { return new URL(url).hostname; } catch { return null; } };
const uniqDomains = (items, getHost) => { const seen = new Set(); return items.map(getHost).filter(d => { if (!d || seen.has(d)) return false; seen.add(d); return true; }); };
const fmtTs = (ts) => { if (!ts || ts.length < 8) return ts; return `${ts.slice(0,4)}-${ts.slice(4,6)}-${ts.slice(6,8)}`; };

// ─── Reusable Components ─────────────────────────────────────────────────────

function ProgressBar({ pct, color = "#00ff88" }) {
  return (
    <div style={{ background: "#1a1a2e", borderRadius: 2, height: 3, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 0.5s ease", boxShadow: `0 0 8px ${color}` }} />
    </div>
  );
}

function Tag({ children, color = "#00ff88" }) {
  return (
    <span style={{ background: `${color}15`, border: `1px solid ${color}40`, color, borderRadius: 3, padding: "1px 7px", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function EmptyState({ icon, msg }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "60px 0", color: "#2d2d5a" }}>
      <span style={{ fontSize: 36, opacity: 0.3 }}>{icon}</span>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{msg}</span>
    </div>
  );
}

function StatGrid({ stats }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 10 }}>
      {stats.map(({ label, value, color }) => (
        <div key={label} style={{ background: "#0d0d1a", border: `1px solid ${color}22`, borderRadius: 8, padding: "11px 12px", textAlign: "center" }}>
          <div style={{ color, fontSize: 22, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{value}</div>
          <div style={{ color: "#4b5563", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── crt.sh Modal ─────────────────────────────────────────────────────────────

function CrtModal({ cert, onClose }) {
  if (!cert) return null;
  const domains = cert.matching_identities?.split("\n").filter(Boolean) || [];
  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#0d0d1a", border: "1px solid #f59e0b44", borderRadius: 12, padding: 28, minWidth: 440, maxWidth: 560, boxShadow: "0 0 60px #f59e0b10" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ color: "#f59e0b", fontFamily: "'IBM Plex Mono', monospace", fontSize: 14 }}>🔐 Cert #{cert.id}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 20 }}>×</button>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {[["Common Name", cert.common_name], ["Issuer", cert.issuer_name], ["Valid From", cert.not_before], ["Expires", cert.not_after]].map(([k, v]) => (
            <div key={k} style={{ background: "#13132b", borderRadius: 6, padding: "9px 12px", display: "flex", justifyContent: "space-between", gap: 16 }}>
              <span style={{ color: "#4b5563", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>{k}</span>
              <span style={{ color: "#e2e8f0", fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", textAlign: "right" }}>{v || "—"}</span>
            </div>
          ))}
          <div style={{ background: "#13132b", borderRadius: 6, padding: "10px 12px" }}>
            <div style={{ color: "#4b5563", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Matching Identities ({domains.length})</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{domains.map(d => <Tag key={d} color="#f59e0b">{d}</Tag>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Geo Modal ────────────────────────────────────────────────────────────────

function GeoModal({ result, onClose }) {
  if (!result) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#0d0d1a", border: "1px solid #00ff8844", borderRadius: 12, padding: 28, minWidth: 460, maxWidth: 580, boxShadow: "0 0 60px #00ff8808" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ color: "#00ff88", fontFamily: "'IBM Plex Mono', monospace", fontSize: 14 }}>{result.domain}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 20 }}>×</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[["IP", result.ip || "—"], ["Status", result.status || "Error"], ["Country", result.country ? `${FLAG(result.country)} ${result.country}` : "—"], ["City", result.city || "—"], ["ASN", result.asn || "—"], ["Org", result.org || "—"], ["Registrar", result.registrar || "—"], ["Created", result.created || "—"], ["Expires", result.expires || "—"], ["Latency", result.latency ? `${result.latency}ms` : "—"], ["SSL", result.ssl ? "✓ Valid" : "✗ None"], ["TLD", result.tld ? `.${result.tld}` : "—"]].map(([k, v]) => (
            <div key={k} style={{ background: "#13132b", borderRadius: 6, padding: "9px 12px" }}>
              <div style={{ color: "#4b5563", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>{k}</div>
              <div style={{ color: "#e2e8f0", fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }}>{v}</div>
            </div>
          ))}
        </div>
        {result.bb_confidence!=null && (
          <div style={{ marginTop:10, background:"#00ff8808", border:"1px solid #00ff8830", borderRadius:6, padding:"10px 12px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ color:"#4b5563", fontSize:10, fontFamily:"'IBM Plex Mono',monospace", letterSpacing:1, textTransform:"uppercase" }}>Barbados Confidence</span>
              <span style={{ color:result.bb_confidence>=70?"#00ff88":result.bb_confidence>=40?"#f59e0b":"#ef4444", fontSize:18, fontFamily:"'IBM Plex Mono',monospace", fontWeight:700 }}>{result.bb_confidence}<span style={{fontSize:11, opacity:0.5}}>/100</span></span>
            </div>
            <div style={{ background:"#1a1a2e", borderRadius:2, height:4, overflow:"hidden", marginBottom:10 }}>
              <div style={{ width:`${result.bb_confidence}%`, height:"100%", background:result.bb_confidence>=70?"#00ff88":result.bb_confidence>=40?"#f59e0b":"#ef4444", transition:"width 0.5s ease" }} />
            </div>
            {result.bb_signals && Object.keys(result.bb_signals).length>0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {Object.entries(result.bb_signals).map(([k,v])=>(
                  <span key={k} style={{ background:"#00ff8812", border:"1px solid #00ff8830", color:"#00ff88", borderRadius:3, padding:"2px 8px", fontSize:10, fontFamily:"'IBM Plex Mono',monospace" }}>
                    {k} +{v}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {result.error && <div style={{ marginTop: 10, background: "#ef444415", border: "1px solid #ef444440", borderRadius: 6, padding: "9px 12px", color: "#ef4444", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>⚠ {result.error}</div>}
      </div>
    </div>
  );
}

// ─── crt.sh Tab ───────────────────────────────────────────────────────────────

function CrtshTab({ tld, results, running, onRun }) {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: 1 });

  const filtered = results.filter(r => !filter || r.common_name?.includes(filter) || r.issuer_name?.includes(filter));
  const sorted = sort.key ? [...filtered].sort((a, b) => { const av = a[sort.key]??''; const bv = b[sort.key]??''; return av<bv?-sort.dir:av>bv?sort.dir:0; }) : filtered;
  const uniqueDomains = uniqDomains(results, r => r.common_name);
  const issuers = [...new Set(results.map(r => r.issuer_name?.split(" ")[0]).filter(Boolean))];

  const TH = ({ k, label }) => (
    <th onClick={() => setSort(s => s.key===k ? {key:k,dir:-s.dir} : {key:k,dir:1})} style={{ padding:"9px 14px", textAlign:"left", color: sort.key===k?"#f59e0b":"#374151", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", borderBottom:"1px solid #1e1e3a", whiteSpace:"nowrap" }}>
      {label}{sort.key===k?(sort.dir>0?" ↑":" ↓"):""}
    </th>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:6, padding:"7px 12px" }}>
            <span style={{ color:"#f59e0b", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>
              https://crt.sh/?q=%.{tld||"bb"}&output=json
            </span>
          </div>
          <button onClick={onRun} disabled={running||!tld} style={{ padding:"7px 18px", background:running?"#1e1e3a":"#f59e0b", border:"none", borderRadius:6, color:running?"#4b5563":"#080812", fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:13, cursor:running||!tld?"not-allowed":"pointer" }}>
            {running?"⟳ Querying...":"Query crt.sh"}
          </button>
        </div>
        {results.length>0 && <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Filter..." style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:6, padding:"6px 12px", color:"#e2e8f0", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, width:180 }} />}
      </div>

      {results.length>0 && (
        <StatGrid stats={[
          {label:"Certificates", value:results.length, color:"#f59e0b"},
          {label:"Unique Domains", value:uniqueDomains.length, color:"#00ff88"},
          {label:"CA Issuers", value:issuers.length, color:"#00aaff"},
          {label:"Wildcards", value:results.filter(r=>r.common_name?.startsWith("*")).length, color:"#a855f7"},
          {label:"Expired", value:results.filter(r=>r.not_after&&new Date(r.not_after)<new Date()).length, color:"#ef4444"},
        ]} />
      )}

      {results.length===0 ? <EmptyState icon="🔐" msg={`Enter a TLD and click "Query crt.sh" to discover certificates`} /> : (
        <div style={{ overflowX:"auto", background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:10 }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:"#0a0a18" }}>
              <TH k="id" label="Cert ID" />
              <TH k="common_name" label="Common Name" />
              <th style={{ padding:"9px 14px", textAlign:"left", color:"#374151", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, letterSpacing:1, textTransform:"uppercase", borderBottom:"1px solid #1e1e3a" }}>SANs</th>
              <TH k="issuer_name" label="Issuer" />
              <TH k="not_before" label="Issued" />
              <TH k="not_after" label="Expires" />
              <th style={{ padding:"9px 14px", borderBottom:"1px solid #1e1e3a" }}></th>
            </tr></thead>
            <tbody>
              {sorted.map((r,i) => {
                const sans = r.matching_identities?.split("\n").filter(Boolean)||[];
                const expired = r.not_after && new Date(r.not_after)<new Date();
                return (
                  <tr key={i} style={{ cursor:"pointer" }} onClick={()=>setSelected(r)}>
                    <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:"#374151" }}>{r.id}</td>
                    <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:"#f59e0b" }}>{r.common_name}</td>
                    <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b" }}>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {sans.slice(0,3).map(d=><Tag key={d} color="#6b7280">{d}</Tag>)}
                        {sans.length>3&&<Tag color="#374151">+{sans.length-3}</Tag>}
                      </div>
                    </td>
                    <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", color:"#6b7280", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.issuer_name}</td>
                    <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", color:"#4b5563", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>{r.not_before}</td>
                    <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b" }}><Tag color={expired?"#ef4444":"#22c55e"}>{r.not_after}</Tag></td>
                    <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", color:"#2d2d5a", fontSize:12 }}>→</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <CrtModal cert={selected} onClose={()=>setSelected(null)} />
    </div>
  );
}

// ─── Common Crawl Tab ─────────────────────────────────────────────────────────

function CommoncrawlTab({ tld, results, crawlId, running, onRun }) {
  const [filter, setFilter] = useState("");
  const [mimeFilter, setMimeFilter] = useState("all");

  const mimes = ["all", ...new Set(results.map(r=>r.mime).filter(Boolean))];
  const filtered = results.filter(r => (mimeFilter==="all"||r.mime===mimeFilter) && (!filter||r.url.includes(filter)));

  const uniqueHosts = uniqDomains(results, r=>hostFromUrl(r.url));
  const s2xx = results.filter(r=>{const s=parseInt(r.status);return s>=200&&s<300;}).length;
  const s3xx = results.filter(r=>{const s=parseInt(r.status);return s>=300&&s<400;}).length;
  const s4xx = results.filter(r=>{const s=parseInt(r.status);return s>=400&&s<500;}).length;

  const exportNDJSON = () => {
    const blob = new Blob([results.map(r=>JSON.stringify(r)).join("\n")], {type:"application/json"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`cc_${tld}_results.json`; a.click();
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:6, padding:"7px 12px", maxWidth:420, overflow:"hidden" }}>
            <span style={{ color:"#00aaff", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>
              {crawlId||"CC-MAIN-latest"}-index?url=*.{tld||"bb"}&output=json&limit=10000
            </span>
          </div>
          <button onClick={onRun} disabled={running||!tld} style={{ padding:"7px 18px", background:running?"#1e1e3a":"#00aaff", border:"none", borderRadius:6, color:running?"#4b5563":"#080812", fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:13, cursor:running||!tld?"not-allowed":"pointer" }}>
            {running?"⟳ Fetching...":"Query Common Crawl"}
          </button>
          {results.length>0&&<button onClick={exportNDJSON} style={{ padding:"7px 14px", background:"#1e1e3a", border:"1px solid #2d2d5a", borderRadius:6, color:"#9ca3af", fontFamily:"'Outfit',sans-serif", fontSize:12, cursor:"pointer" }}>↓ NDJSON</button>}
        </div>
        {results.length>0&&(
          <div style={{ display:"flex", gap:8 }}>
            <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Filter URL..." style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:6, padding:"6px 12px", color:"#e2e8f0", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, width:160 }} />
            <select value={mimeFilter} onChange={e=>setMimeFilter(e.target.value)} style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:6, padding:"6px 10px", color:"#9ca3af", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>
              {mimes.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
      </div>

      {results.length>0&&(
        <>
          <StatGrid stats={[
            {label:"URLs Found", value:results.length, color:"#00aaff"},
            {label:"Unique Hosts", value:uniqueHosts.length, color:"#00ff88"},
            {label:"2xx", value:s2xx, color:"#22c55e"},
            {label:"3xx", value:s3xx, color:"#f59e0b"},
            {label:"4xx/Errors", value:s4xx, color:"#ef4444"},
          ]} />
          {crawlId&&<div style={{ background:"#00aaff08", border:"1px solid #00aaff20", borderRadius:6, padding:"8px 12px", color:"#00aaff88", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>
            📦 Crawl: <span style={{color:"#00aaff"}}>{crawlId}</span> &nbsp;·&nbsp; {results.length.toLocaleString()} records &nbsp;·&nbsp; saved to <span style={{color:"#00aaff"}}>cc_results.json</span>
          </div>}
        </>
      )}

      {results.length===0 ? <EmptyState icon="🕸️" msg={`Enter a TLD and click "Query Common Crawl" to discover indexed URLs`} /> : (
        <div style={{ overflowX:"auto", background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:10 }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:"#0a0a18" }}>
              {["URL","Status","MIME","Languages","Crawled"].map(h=>(
                <th key={h} style={{ padding:"9px 14px", textAlign:"left", color:"#374151", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, letterSpacing:1, textTransform:"uppercase", borderBottom:"1px solid #1e1e3a", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map((r,i)=>(
                <tr key={i}>
                  <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:"#00aaff", maxWidth:280, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.url}</td>
                  <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b" }}><Tag color={statusColor(parseInt(r.status))}>{r.status}</Tag></td>
                  <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", color:"#6b7280", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>{r.mime||"—"}</td>
                  <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", color:"#4b5563", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>{r.languages||"—"}</td>
                  <td style={{ padding:"9px 14px", borderBottom:"1px solid #13132b", color:"#2d2d5a", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>{fmtTs(r.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Geo Enrichment Tab ───────────────────────────────────────────────────────

function GeoTab({ tld, results, running, progress, stepIdx, processingDomain, logs, onRun, logRef }) {
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState({ key:null, dir:1 });
  const [selected, setSelected] = useState(null);
  const STEPS = ["Resolving DNS","Fetching HTTP","GeoIP Lookup","WHOIS Query","Complete"];

  const filtered = results.filter(r => !filter || r.domain.includes(filter) || (r.ip||"").includes(filter) || (r.country||"").includes(filter));
  const sorted = sort.key ? [...filtered].sort((a,b)=>{ const av=a[sort.key]??""; const bv=b[sort.key]??""; return av<bv?-sort.dir:av>bv?sort.dir:0; }) : filtered;

  const TH = ({k,label}) => (
    <th onClick={()=>setSort(s=>s.key===k?{key:k,dir:-s.dir}:{key:k,dir:1})} style={{ padding:"9px 14px", textAlign:"left", color:sort.key===k?"#00ff88":"#374151", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, letterSpacing:1, textTransform:"uppercase", cursor:"pointer", borderBottom:"1px solid #1e1e3a", whiteSpace:"nowrap" }}>
      {label}{sort.key===k?(sort.dir>0?" ↑":" ↓"):""}
    </th>
  );

  const exportCSV = () => {
    const cols = ["domain","ip","status","country","city","asn","org","registrar","created","expires","latency","ssl"];
    const rows = results.map(r=>cols.map(c=>r[c]??"").join(","));
    const blob = new Blob([cols.join(",")+"\\n"+rows.join("\\n")],{type:"text/csv"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`geo_${tld}.csv`; a.click();
  };

  const local = results.filter(r=>r.country===tld?.toUpperCase()).length;
  const onCF = results.filter(r=>r.asn?.includes("Cloudflare")).length;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:6, padding:"7px 12px" }}>
            <span style={{ color:"#00ff88", fontFamily:"'IBM Plex Mono',monospace", fontSize:11 }}>
              python bb_geo_enrich.py enrich --db bb_index.db{tld?` --tld .${tld}`:""}
            </span>
          </div>
          <button onClick={onRun} disabled={running} style={{ padding:"7px 18px", background:running?"#1e1e3a":"linear-gradient(135deg,#00ff88,#00cc70)", border:"none", borderRadius:6, color:running?"#4b5563":"#080812", fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:13, cursor:running?"not-allowed":"pointer" }}>
            {running?"⟳ Enriching...":results.length?"↺ Re-Enrich":"▶ Run Enrichment"}
          </button>
          {results.length>0&&<button onClick={exportCSV} style={{ padding:"7px 14px", background:"#1e1e3a", border:"1px solid #2d2d5a", borderRadius:6, color:"#9ca3af", fontFamily:"'Outfit',sans-serif", fontSize:12, cursor:"pointer" }}>↓ CSV</button>}
        </div>
        {results.length>0&&<input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Filter..." style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:6, padding:"6px 12px", color:"#e2e8f0", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, width:180 }} />}
      </div>

      {running&&(
        <div style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:8, padding:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ color:"#9ca3af", fontSize:11, fontFamily:"'IBM Plex Mono',monospace" }}>{STEPS[stepIdx]}</span>
            <span style={{ color:"#00ff88", fontSize:11, fontFamily:"'IBM Plex Mono',monospace" }}>{progress}%</span>
          </div>
          <ProgressBar pct={progress} />
          <div style={{ marginTop:8, color:"#f59e0b", fontSize:11, fontFamily:"'IBM Plex Mono',monospace" }}>→ {processingDomain}</div>
        </div>
      )}

      {results.length>0&&(
        <StatGrid stats={[
          {label:"Total", value:results.length, color:"#00aaff"},
          {label:"Resolved", value:results.filter(r=>!r.error).length, color:"#00ff88"},
          {label:"Errors", value:results.filter(r=>r.error).length, color:"#ef4444"},
          {label:"On Cloudflare", value:onCF, color:"#f59e0b"},
          {label:"Local Hosting", value:local, color:"#a855f7"},
        ]} />
      )}

      {logs.length>0&&(
        <div style={{ background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:8, padding:12 }}>
          <div style={{ color:"#2d2d5a", fontSize:10, fontFamily:"'IBM Plex Mono',monospace", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Activity Log</div>
          <div ref={logRef} style={{ height:72, overflowY:"auto", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, lineHeight:1.9 }}>
            {logs.map((l,i)=><div key={i} style={{color:l.color}}><span style={{color:"#2d2d5a"}}>{l.ts} </span>{l.msg}</div>)}
          </div>
        </div>
      )}

      {results.length===0 ? <EmptyState icon="🌍" msg="Run enrichment to get geo + WHOIS data for all discovered domains" /> : (
        <div style={{ overflowX:"auto", background:"#0d0d1a", border:"1px solid #1e1e3a", borderRadius:10 }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:"#0a0a18" }}>
              <TH k="domain" label="Domain" />
              <TH k="ip" label="IP" />
              <TH k="status" label="Status" />
              <TH k="country" label="Geo" />
              <TH k="asn" label="ASN" />
              <TH k="org" label="Org" />
              <TH k="latency" label="ms" />
              <TH k="bb_confidence" label="BB%" />
              <th style={{ padding:"9px 14px", borderBottom:"1px solid #1e1e3a" }}></th>
            </tr></thead>
            <tbody>
              {sorted.map((r,i)=>(
                <tr key={i} style={{ cursor:"pointer" }} onClick={()=>setSelected(r)}>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b", fontFamily:"'IBM Plex Mono',monospace", fontSize:12, color:r.error?"#ef4444":"#00ff88" }}>
                    {r.ssl&&!r.error&&<span style={{marginRight:4}}>🔒</span>}{r.domain}
                  </td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:"#9ca3af" }}>{r.ip||"—"}</td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b" }}>{r.status?<Tag color={statusColor(r.status)}>{r.status}</Tag>:<Tag color="#ef4444">ERR</Tag>}</td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b", fontFamily:"'IBM Plex Mono',monospace", fontSize:12 }}>{r.country?`${FLAG(r.country)} ${r.country}`:"—"}</td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b", color:"#6b7280", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.asn||"—"}</td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b", color:"#6b7280", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, maxWidth:130, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.org||"—"}</td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b" }}>
                    {r.latency?<span style={{ color:latencyColor(r.latency), fontFamily:"'IBM Plex Mono',monospace", fontSize:12 }}>{r.latency}</span>:<span style={{color:"#2d2d5a"}}>—</span>}
                  </td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b" }}>
                    {r.bb_confidence!=null
                      ? <span style={{ color: r.bb_confidence>=70?"#00ff88":r.bb_confidence>=40?"#f59e0b":"#ef4444", fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:700 }}>{r.bb_confidence}</span>
                      : <span style={{color:"#2d2d5a"}}>—</span>}
                  </td>
                  <td style={{ padding:"10px 14px", borderBottom:"1px solid #13132b", color:"#2d2d5a", fontSize:12 }}>→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <GeoModal result={selected} onClose={()=>setSelected(null)} />
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [tld, setTld] = useState("bb");
  const [activeTab, setActiveTab] = useState("crtsh");

  const [crtResults, setCrtResults] = useState([]);
  const [crtRunning, setCrtRunning] = useState(false);

  const [ccResults, setCcResults] = useState([]);
  const [ccRunning, setCcRunning] = useState(false);
  const [crawlId, setCrawlId] = useState("");

  const [geoResults, setGeoResults] = useState([]);
  const [geoRunning, setGeoRunning] = useState(false);
  const [geoProgress, setGeoProgress] = useState(0);
  const [geoStep, setGeoStep] = useState(0);
  const [geoProcessing, setGeoProcessing] = useState("");
  const [geoLogs, setGeoLogs] = useState([]);
  const logRef = useRef(null);

  const STEPS = ["Resolving DNS","Fetching HTTP","GeoIP Lookup","WHOIS Query","Complete"];
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [geoLogs]);

  const addLog = (msg, type="info") => {
    const colors = {info:"#6b7280",success:"#00ff88",error:"#ef4444",step:"#f59e0b"};
    setGeoLogs(l=>[...l, {msg, color:colors[type], ts:new Date().toLocaleTimeString("en",{hour12:false})}]);
  };

  const runCrtsh = async () => {
    setCrtRunning(true); setCrtResults([]);
    try {
      const res = await fetch(`/api/crtsh?tld=${tld}`);
      const data = await res.json();
      setCrtResults(data.results || []);
    } catch(e) {
      console.error("crt.sh error:", e);
    }
    setCrtRunning(false);
  };

  const runCC = async () => {
    setCcRunning(true); setCcResults([]);
    try {
      const res = await fetch(`/api/commoncrawl?tld=${tld}&limit=10000`);
      const data = await res.json();
      setCrawlId(data.crawl_id || "");
      setCcResults(data.results || []);
    } catch(e) {
      console.error("Common Crawl error:", e);
    }
    setCcRunning(false);
  };

  const runGeo = async () => {
    setGeoRunning(true); setGeoResults([]); setGeoLogs([]); setGeoProgress(0); setGeoStep(0);

    const fromCrt = crtResults.map(r=>r.common_name).filter(d=>d&&!d.startsWith("*"));
    const fromCC = uniqDomains(ccResults, r=>hostFromUrl(r.url));
    const domains = [...new Set([...fromCrt,...fromCC])].filter(Boolean);

    if (!domains.length) {
      addLog("No domains found — run crt.sh or Common Crawl first", "error");
      setGeoRunning(false);
      return;
    }

    addLog(`Loaded ${domains.length} targets from crt.sh + Common Crawl`, "info");

    try {
      const res = await fetch("/api/enrich/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.done) {
              setGeoStep(4);
              addLog(`✓ Done — ${domains.length} domains enriched`, "success");
              continue;
            }
            const prog = payload._progress || {};
            delete payload._progress;
            setGeoResults(prev => [...prev, payload]);
            setGeoProcessing(payload.domain);
            setGeoProgress(prog.pct || 0);
            setGeoStep(prog.done < prog.total ? 1 : 4);
            payload.error
              ? addLog(`[${payload.domain}] ✗ ${payload.error}`, "error")
              : addLog(`[${payload.domain}] ✓ ${payload.ip||"?"} — ${payload.country||"?"} — confidence: ${payload.bb_confidence??"-"}`, "success");
          } catch(e) {}
        }
      }
    } catch(e) {
      addLog(`Stream error: ${e.message}`, "error");
    }

    setGeoRunning(false);
  };

  const BB_SLDS = ["bb","com.bb","net.bb","org.bb","gov.bb","co.bb","biz.bb","store.bb","tv.bb","info.bb"];
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState([]);

  const runAllBBSlds = async () => {
    setBatchRunning(true);
    setBatchProgress([]);
    for (const sld of BB_SLDS) {
      setBatchProgress(p => [...p, {sld, status:"running"}]);
      try {
        const res = await fetch(`/api/crtsh?tld=${sld}`);
        const data = await res.json();
        setBatchProgress(p => p.map(x => x.sld===sld ? {...x, status:"done", count:data.count||0} : x));
      } catch(e) {
        setBatchProgress(p => p.map(x => x.sld===sld ? {...x, status:"error"} : x));
      }
    }
    setBatchRunning(false);
  };

  const pipelineSteps = [
    {id:"crtsh", label:"crt.sh", done:crtResults.length>0, running:crtRunning, color:"#f59e0b", count:crtResults.length},
    {id:"cc", label:"Common Crawl", done:ccResults.length>0, running:ccRunning, color:"#00aaff", count:ccResults.length},
    {id:"geo", label:"Geo Enrich", done:geoResults.length>0, running:geoRunning, color:"#00ff88", count:geoResults.length},
  ];

  const tabs = [
    {id:"crtsh", label:"🔐  crt.sh", color:"#f59e0b", count:crtResults.length},
    {id:"cc", label:"🕸️  Common Crawl", color:"#00aaff", count:ccResults.length},
    {id:"geo", label:"🌍  Geo Enrichment", color:"#00ff88", count:geoResults.length},
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Outfit:wght@300;400;600;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        body { background:#080812; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:#0d0d1a; }
        ::-webkit-scrollbar-thumb { background:#1e1e3a; border-radius:2px; }
        tr:hover td { background:#13132b !important; transition:background 0.1s; }
        input:focus,select:focus { outline:none; border-color:#2d2d5a !important; }
        button { transition:all 0.15s; }
        button:not(:disabled):active { transform:scale(0.97); }
      `}</style>

      <div style={{ minHeight:"100vh", background:"#080812", color:"#e2e8f0", fontFamily:"'Outfit',sans-serif", display:"flex", flexDirection:"column" }}>

        {/* Header */}
        <div style={{ borderBottom:"1px solid #1e1e3a", padding:"13px 24px", display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:30, height:30, background:"linear-gradient(135deg,#00ff88,#00aaff)", borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🌐</div>
            <div>
              <div style={{ fontWeight:700, fontSize:14, letterSpacing:-0.3 }}>DomainScope</div>
              <div style={{ color:"#4b5563", fontSize:10, fontFamily:"'IBM Plex Mono',monospace" }}>crt.sh · commoncrawl · geo enrichment</div>
            </div>
          </div>

          {/* TLD Input */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, maxWidth:340 }}>
            <span style={{ color:"#4b5563", fontFamily:"'IBM Plex Mono',monospace", fontSize:12, whiteSpace:"nowrap" }}>Target TLD</span>
            <div style={{ position:"relative", flex:1 }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#4b5563", fontFamily:"'IBM Plex Mono',monospace", fontSize:13 }}>.</span>
              <input value={tld} onChange={e=>setTld(e.target.value.replace(/[^a-z0-9-]/gi,"").toLowerCase())} placeholder="bb"
                style={{ width:"100%", background:"#0d0d1a", border:"1px solid #2d2d5a", borderRadius:6, padding:"7px 10px 7px 20px", color:"#00ff88", fontFamily:"'IBM Plex Mono',monospace", fontSize:14, letterSpacing:1 }} />
            </div>
            <Tag color={tld?"#00ff88":"#2d2d5a"}>{tld?`*.${tld}`:"set tld"}</Tag>
          </div>

          {/* Batch discovery */}
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={runAllBBSlds} disabled={batchRunning} style={{ padding:"7px 14px", background:batchRunning?"#1e1e3a":"#f59e0b22", border:"1px solid #f59e0b55", borderRadius:6, color:batchRunning?"#4b5563":"#f59e0b", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, cursor:batchRunning?"not-allowed":"pointer", whiteSpace:"nowrap" }}>
              {batchRunning ? "⟳ running..." : "⚡ all .bb SLDs"}
            </button>
            {batchProgress.length>0&&(
              <div style={{ display:"flex", gap:3 }}>
                {batchProgress.map(x=>(
                  <div key={x.sld} title={`.${x.sld}${x.count!=null?" · "+x.count+" certs":""}`} style={{ width:8, height:8, borderRadius:"50%", background:x.status==="done"?"#00ff88":x.status==="error"?"#ef4444":"#f59e0b", boxShadow:x.status==="running"?"0 0 5px #f59e0b":"none" }} />
                ))}
              </div>
            )}
          </div>

          {/* Pipeline status dots */}
          <div style={{ display:"flex", gap:6, alignItems:"center", marginLeft:"auto" }}>
            {pipelineSteps.map((s,i)=>(
              <div key={s.id} style={{ display:"flex", alignItems:"center", gap:6 }}>
                {i>0&&<span style={{color:"#1e1e3a",fontSize:10}}>→</span>}
                <div style={{ background:"#0d0d1a", border:`1px solid ${s.done?s.color+"50":"#1e1e3a"}`, borderRadius:5, padding:"3px 9px", display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", background:s.running?"#f59e0b":s.done?s.color:"#2d2d5a", boxShadow:(s.running||s.done)?`0 0 5px ${s.running?"#f59e0b":s.color}`:"none" }} />
                  <span style={{ color:s.done?s.color:"#4b5563", fontSize:10, fontFamily:"'IBM Plex Mono',monospace" }}>{s.label}</span>
                  {s.done&&<span style={{ color:`${s.color}99`, fontSize:9, fontFamily:"'IBM Plex Mono',monospace" }}>{s.count}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom:"1px solid #1e1e3a", padding:"0 24px", display:"flex" }}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{ padding:"11px 20px", background:"none", border:"none", borderBottom:activeTab===t.id?`2px solid ${t.color}`:"2px solid transparent", color:activeTab===t.id?t.color:"#4b5563", fontFamily:"'Outfit',sans-serif", fontSize:13, fontWeight:activeTab===t.id?600:400, cursor:"pointer", display:"flex", alignItems:"center", gap:7, marginBottom:-1 }}>
              {t.label}
              {t.count>0&&<Tag color={t.color}>{t.count}</Tag>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex:1, padding:22, overflow:"auto" }}>
          {activeTab==="crtsh"&&<CrtshTab tld={tld} results={crtResults} running={crtRunning} onRun={runCrtsh} />}
          {activeTab==="cc"&&<CommoncrawlTab tld={tld} results={ccResults} crawlId={crawlId} running={ccRunning} onRun={runCC} />}
          {activeTab==="geo"&&<GeoTab tld={tld} results={geoResults} running={geoRunning} progress={geoProgress} stepIdx={geoStep} processingDomain={geoProcessing} logs={geoLogs} onRun={runGeo} logRef={logRef} />}
        </div>
      </div>
    </>
  );
}
